import { Evaluation, IComparerBody } from '@api/evaluation/evaluation.model';
import { Sport, SportPosition, Gender } from '@api/domain';
import { PersonDTO } from '@api/patient/evaluation/models/personDTO';

export class EvaluationReport {
  id: number;
  title: string;
  evaluationType: string;
  patient?: ReportPatientInfo;
  basicData?: BasicData;
  generationDate?: string;
  currentEvaluationDate: string;
  lastEvaluationDate: string;
}
export class ReportPatientInfo {
  id: number;
  name: string;
  surname: string;
  age: number;
  urlAvatarProfile: string;
}

export class MassInformation {
  percentile: number;
  quantity: number;
  scoreZ: number;
  difference: number;
}

export class HeathCarterSomatotype {
  endomorphy: number;
  mesomorphy: number;
  ectomorphy: number;
  coordinateX: number;
  coordinateY: number;
}

export class CalculatedValues {
  fatMass: MassInformation;
  muscleMass: MassInformation;
  residualMass: MassInformation;
  boneMass: MassInformation;
  skinMass: MassInformation;
  totalMass: MassInformation;
  percStructuredWeightVsGrossWeight: number;
  foldsSum: number;
  muscleBoneIndex: number;
  bodyMassIndex: number;
  fatMuscleIndex: number;
  sittingHeightIndex: number;
  bodySurfaceArea: number;
  bodySurfaceAreaWeight: number;
  heathCarterSomatotype: HeathCarterSomatotype;
  fatMassOfSecond?: MassInformation;
  muscleMassOfSecond?: MassInformation;
  residualMassOfSecond?: MassInformation;
  boneMassOfSecond?: MassInformation;
  skinMassOfSecond?: MassInformation;
  totalMassOfSecond?: MassInformation;
}

export class BasicaAnthropometricReport extends EvaluationReport {
  currentEvaluation: Evaluation;
  differenceWithPreviousEvaluation: Evaluation;
  scoreZValues: Evaluation;
  calculatedValues: CalculatedValues;
}

export class ReportClinico {
  generationDate: string;
  person: PersonDTO;
  medition: {
    creationDate: string | null;
    basicMeasurement: BasicMeasurementDTO;
  };
  title: string;
  weightTimeline: { date: string; medition: number }[];
}

export class ClinicoMedition {
  creationDate: string;
  basicMeasurement: BasicMeasurementDTO;
}

export class BasicMeasurementDTO {
  id: number;
  weight: number;
  height: number;
  waist: number;
  standingHeight: number;
  sittingHeight: number;
  wingspan: number;
}
export class ComparativeReport extends EvaluationReport {
  selectedEvaluation1: EvaluationDTO;
  selectedEvaluation2: EvaluationDTO;
  diferenceBetween: EvaluationDTO;
  calculatedValues: CalculatedValues;
}

export class TimelineReport extends EvaluationReport {
  generationDate: string;
  person: Person;
  weightTimeline: TimelineItem[];
  sixFoldTimeline: TimelineItem[];
  bodyMassIndexTimeline: TimelineItem[];
  kgMATimeline: TimelineItem[];
  kgMMTimeline: TimelineItem[];
  mlgPercentile?: TimelineItem[];
  imlgt?: TimelineItem[];
  mgPercentile?: TimelineItem[];
}

export class TimelineItem {
  date: string;
  medition: number;
}

export class EvaluationDTO {
  evaluationType: string;
  basicMeasurement: BasicMeditionEvaluateDTO;
  segmentalLengthsAndHeightsMeasurement: SegmentalLengthsAndHeightsMeasurementDto;
  diameterMeasurement: DiameterMeasurementDTO;
  perimeterMeasurement: perimeterMeasurementDTO;
  foldMeasurement: FoldMeditionDTO;
  anthopometric: any;
  bodyComposition: any;
  creationDate: string;
}
export class BasicData {
  person: Person;
  bmi: number;
  weight: number;
  height: number;
  sport?: Sport;
  position?: SportPosition;
}

export class DiameterMeasurementDTO {
  biacromial: number;
  transverseChest: number;
  anteroposteriorChest: number;
  biIliocrestide: number;
  humera: number;
  femoral: number;
  wrist: number;
  hand: number;
  ankle?: number;
}

export class perimeterMeasurementDTO {
  relaxedArm: number;
  flexedArm: number;
  maximumForearm: number;
  thoraxMesoSternal: number;
  minimumWaist: number;
  maxHip: number;
  maximumThigh: number;
  mediumThigh: number;
  maximumCalf: number;
  head: number;
  neck: number;
  wrist: number;
  omphalicAbomina: number;
  minimumAnkle: number;
}
export class SegmentalLengthsAndHeightsMeasurementDto {
  radialAcromial: number;
  radialStyloid: number;
  medialStyloidFingerprint: number;
  iliospinalHeight: number;
  trocantereaHeight: number;
  trochanterFibialLat: number;
  tibialLateralHeight: number;
  tibialMedialMaleolarMedial: number;
  foot: number;
}

export class Person {
  id: number;
  name: string;
  surname: string;
  age: number;
  urlAvatarProfile: string;
  birthday: string;
  gender: Gender;
  sport?: Sport;
  position?: SportPosition;
}

export class DiagnosticData {
  massMusculeDiagnostic: ReferenceValue;
  massFatDiagnostic: ReferenceValue;
  massBoneDiagnostic: ReferenceValue;
  pointMassMuscule: number;
  pointMassFate: number;
  pointMassBone: number;
}

export class ReferenceValue {
  currentValue: number;
  referenceValue: number;
  minReferenceValue: number;
  maxRReferenceValue: number;
}

export class AjustmentStructureBone {
  ajustmentMassFatForBoneMassInKG: number;
  ajustmentMassMusculeForMassBoneInKG: number;
  indexBoneMass: ReferenceValue;
  scoreComposition: number;
  summationSixFold: ReferenceValue;
  ajustmentByHeightSentence: number;
  referencialHeigh: number;
}

export class SportsmanGoal {
  maxMassMusculeValue: number;
  minMassFatValue: number;
  summationSixFoldGoal: number;
  minRangeOptimumWeight: number;
  maxRagneOptimumWeight: number;
}

export class ReferenceOfPostPresent {
  sentence1: number;
  sentence2: number;
  sentence3: number;
}

export class ClinicoReport extends EvaluationReport {}

export class ComparativeVsReferencialReport extends EvaluationReport {
  generationDate: string;
  diagnosticData: DiagnosticData;
  ajustmentStructureBone: AjustmentStructureBone;
  sportsmanGoal: SportsmanGoal;
  referencePostPresent: ReferenceOfPostPresent;
  graphIndexStatusDTO: GraphIndexStatusDTO;
}

export interface ScreenningReportDataDTO extends EvaluationReport {
  diferenceFoldsMedition: FoldMeditionDTO;
  diferenceImc: number;
  diferenceSummatorySixFold: number;
  foldMeditionEvaluate1: FoldMeditionDTO;
  foldMeditionEvaluate2: FoldMeditionDTO;
  basicMeditionEvaluate1: BasicMeditionEvaluateDTO;
  basicMeditionEvaluate2: BasicMeditionEvaluateDTO;
  generationDate: string;
  gralControlChart: ControlChartDTO;
  imc1: number;
  imc2: number;
  diferenceWeight: number;
  maxGoalWeight: number;
  minGoalWeight: number;
  person: Person;
  summarySixFold1: number;
  summarySixFold2: number;
  isSimple: boolean;
  sixFoldCorrect: number;
  sixFoldIdeal: number;
}

export interface BasicMeditionEvaluateDTO {
  height: number;
  id: number;
  sittingHeight: number;
  standingHeight: number;
  weight: number;
  wingspan: number;
}

export interface ControlChartDTO {
  lowRangeBMI: number;
  upperRange6Fold: number;
  pointX: number;
  pointY: number;
}
export interface FoldMeditionDTO {
  triceps: number;
  subscapular: number;
  supraSpinal: number;
  abdominal: number;
  medialThigh: number;
  calf: number;
  bicep?: number;
  iliacCrest: number;
}
export class ReportType {
  code: string;
  description: string;
}

export class ReportTypes {
  static BodyComposition: ReportType = {
    code: 'basic-anthropometric',
    description: '',
  };
  static AnthropometricEvaluation: ReportType = {
    code: 'comparative-vs-referencial-report',
    description: '',
  };

  static AnthropometricDiagnosisSimple: ReportType = {
    code: 'screenning-simple-report',
    description: '',
  };
  static ComparativeReport: ReportType = {
    code: 'comparative-report',
    description: '',
  };
  static TimelineReport: ReportType = {
    code: 'timeline-report',
    description: '',
  };
  static BiadexaReport: ReportType = {
    code: 'bia-dxa-report',
    description: '',
  };
  static CompositionCorporal: ReportType = {
    code: 'body-composition',
    description: '',
  };
  static ClinicoReport: ReportType = {
    code: 'clinico',
    description: '',
  };
  static AnthropometricDiagnosis: ReportType = {
    code: 'anthropometric-diagnosis',
    description: '',
  };
  static ALL: ReportType[] = [ReportTypes.BodyComposition];
}

export class PatientMultipleReportRecord {
  evaluationId: number;
  name: string;
  surname: string;
  personId: number;
  evaluationDate: string;
  comparerBody: IComparerBody;
}

export class GraphIndexStatusDTO {
  indexMOinit: number;
  indexMOPoor: number;
  indexMODeficient: number;
  indexMOGood: number;
  indexMOExcelent: number;
  indexMOEnd: number;
  sixFoldInit: number;
  sixFoldExcelent: number;
  sixFoldVeryGood: number;
  sixFoldRegular: number;
  sixFoldPoor: number;
  sixFoldEnd: number;
}

export interface SuscriptionState {
  id: number;
  creationDate: string;
  accountName: string;
  licenceId: number;
  name: string;
  surname: string;
  birthday: string;
  numberDocument: string;
  email: string;
  documentCode: string;
  genderCode: string;
  telephoneTypeID: number;
  telephoneAreaCode: number;
  telephoneNumber: number;
  addressCountry: string;
  addressProvince: string;
  addressLocation: string;
  addressFloor: string;
  addressZipCode: string;
  addressStreetNumber: number;
  addressStreet: string;
  addressStreet1: string;
  addressStreet2: string;
  addressDeparture: string;
  stateCopyReferencial: string;
  stateCopyFood: string;
  stateCopyHistory: string;
  state: string;
  reason: string;
  accountId: number;
  personId: number;
  operationId: string;
  expireDate: string;
}

export interface ExecuteActionDto {
  actionEnum: string;
  selectedSuscriptions: number[];
}

export interface RenewalStateItem {
  id: number;
  state?: string | null;
  accountName?: string | null;
  email?: string | null;
  previousLicenceId?: number | null;
  previousLicenceName?: string | null;
  newLicenceId?: number | null;
  newLicenceName?: string | null;
  newLicencePrice?: number | null;
  discountAmount?: number | null;
  offerName?: string | null;
  remainingCredit?: number | null;
  amount?: number | null;
  creationDate?: string | null;
  previousExpireDate?: string | null;
  newExpireDate?: string | null;
  mpPaymentId?: string | null;
  externalReference?: string | null;
}

export type RenewalState = RenewalStateItem[];

export interface ExecuteRenewalActionDto {
  actionEnum: 'APPROVE' | 'DENY';
  selectedRenewals: number[];
}

export interface SuscriptionSummaryDTO {
  accountId: number;
  accountName: string;
  creationDate: string;
  email: string;
  expireDate: string;
  id: number;
  licenceId: number;
  name: string;
  state: string;
  surname: string;
}
